import React, { useMemo } from 'react';
import { RiderDto } from '@/features/partner/supplierFee/riderToPartner/types/supplierFee.types';
import {
    Mission, MissionType, PeriodType, FormState,
    MissionTypeCode, PeriodCode,
} from '../types/promotion.types';
import {
    getMissionMeta, getPeriodName,
    buildPreviewMission, buildPreviewSchedule, buildPreviewReward,
} from '../utils/promotionHelpers';
import '../styles/MissionRegisterForm.css';

type FieldChangeHandler = <K extends keyof FormState>(key: K, value: FormState[K]) => void;

interface MissionRegisterFormProps {
    form: FormState;
    editingSeqNo: number | null;
    missionTypeCodes: MissionTypeCode[];
    periodCodes: PeriodCode[];
    riders: RiderDto[];
    ridersLoading: boolean;
    ridersLoaded: boolean;
    riderSearch: string;
    isValid: boolean;
    today: string;
    onMissionTypeChange: (code: MissionType) => void;
    onFieldChange: FieldChangeHandler;
    onRiderSearchChange: (s: string) => void;
    onToggleRider: (riderId: string) => void;
    onFetchRiders: () => Promise<void>;
    onRegister: () => Promise<void>;
    onUpdate: () => Promise<void>;
    onReset: () => void;
}

const MissionRegisterForm: React.FC<MissionRegisterFormProps> = ({
                                                                     form,
                                                                     editingSeqNo,
                                                                     missionTypeCodes,
                                                                     periodCodes,
                                                                     riders,
                                                                     ridersLoading,
                                                                     riderSearch,
                                                                     isValid,
                                                                     today,
                                                                     onMissionTypeChange,
                                                                     onFieldChange,
                                                                     onRiderSearchChange,
                                                                     onToggleRider,
                                                                     onFetchRiders,
                                                                     onRegister,
                                                                     onUpdate,
                                                                     onReset,
                                                                 }) => {
    const filteredRiders = useMemo(
        () => riders.filter((r) => r.riderName.includes(riderSearch) || r.riderId.includes(riderSearch)),
        [riders, riderSearch]
    );

    const currentMeta = getMissionMeta(form.missionType, missionTypeCodes);
    const previewMission = buildPreviewMission(form, missionTypeCodes, periodCodes);
    const previewSchedule = buildPreviewSchedule(form, periodCodes);
    const previewReward = buildPreviewReward(form);

    return (
        <div className="filter-section mission-register-section">
            <div className="mission-section-title">
                {editingSeqNo ? '미션 수정' : '새 미션 등록'}
            </div>

            {/* 미션 유형 선택 */}
            <div className="mission-type-row">
                {missionTypeCodes.map((type) => (
                    <button
                        key={type.code}
                        type="button"
                        className={`mission-type-card${form.missionType === type.code ? ' selected' : ''}`}
                        onClick={() => onMissionTypeChange(type.code)}
                    >
                        <span className="mission-type-label">{type.name}</span>
                        <span className="mission-type-desc">{type.desc}</span>
                    </button>
                ))}
            </div>

            {/* 입력 폼 */}
            <div className="mission-form-grid">
                <div className="mission-field full-width">
                    <label className="mission-label">
                        미션 제목 <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        className="mission-input"
                        placeholder="예) 5월 일일 50건 챌린지"
                        value={form.title}
                        onChange={(e) => onFieldChange('title', e.target.value)}
                        maxLength={100}
                    />
                </div>

                <div className="mission-field">
                    <label className="mission-label">기간</label>
                    <select
                        className="mission-select"
                        value={form.period}
                        onChange={(e) => onFieldChange('period', e.target.value as PeriodType)}
                    >
                        {periodCodes.map((p) => (
                            <option key={p.code} value={p.code}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mission-field">
                    <label className="mission-label">
                        목표 건수 <span className="required">*</span>
                    </label>
                    <div className="mission-input-group">
                        <input
                            type="number"
                            className="mission-input"
                            placeholder="0"
                            min={1}
                            value={form.targetCount}
                            onChange={(e) => onFieldChange('targetCount', e.target.value)}
                        />
                        <span className="mission-unit">건</span>
                    </div>
                </div>

                <div className="mission-field">
                    <label className="mission-label">
                        {currentMeta.rewardLabel} <span className="required">*</span>
                    </label>
                    <div className="mission-input-group">
                        <input
                            type="number"
                            className="mission-input"
                            placeholder="0"
                            min={1}
                            value={form.rewardAmount}
                            onChange={(e) => onFieldChange('rewardAmount', e.target.value)}
                        />
                        <span className="mission-unit">{currentMeta.rewardUnit}</span>
                    </div>
                </div>

                {form.missionType !== 'FIXED' && (
                    <div className="mission-field">
                        <label className="mission-label">
                            최대 인정 건수 <span className="mission-hint">(0=무제한)</span>
                        </label>
                        <div className="mission-input-group">
                            <input
                                type="number"
                                className="mission-input"
                                placeholder="0"
                                min={0}
                                value={form.maxCount}
                                onChange={(e) => onFieldChange('maxCount', e.target.value)}
                            />
                            <span className="mission-unit">건</span>
                        </div>
                    </div>
                )}

                <div className="mission-field">
                    <label className="mission-label">
                        시작일 <span className="required">*</span>
                    </label>
                    <input
                        type="date"
                        className="mission-input"
                        value={form.startDate}
                        min={today}
                        onChange={(e) => onFieldChange('startDate', e.target.value)}
                    />
                </div>

                <div className="mission-field">
                    <label className="mission-label">
                        종료일 <span className="required">*</span>
                    </label>
                    <input
                        type="date"
                        className="mission-input"
                        value={form.endDate}
                        min={form.startDate || today}
                        onChange={(e) => onFieldChange('endDate', e.target.value)}
                    />
                </div>

                <div className="mission-field full-width">
                    <label className="mission-label">
                        설명 <span className="mission-hint">(선택)</span>
                    </label>
                    <textarea
                        className="mission-textarea"
                        placeholder="라이더에게 안내할 추가 설명"
                        rows={2}
                        value={form.description}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        maxLength={300}
                    />
                </div>
            </div>

            {/* 대상 라이더 선택 */}
            <div className="mission-target-section">
                <label className="mission-label">대상 라이더</label>
                <div className="mission-radio-row">
                    <label className="mission-radio-label">
                        <input
                            type="radio"
                            value="ALL"
                            checked={form.targetType === 'ALL'}
                            onChange={() => onFieldChange('targetType', 'ALL')}
                        />
                        협력사 전체 라이더
                    </label>
                    <label className="mission-radio-label">
                        <input
                            type="radio"
                            value="SPECIFIC"
                            checked={form.targetType === 'SPECIFIC'}
                            onChange={() => {
                                onFieldChange('targetType', 'SPECIFIC');
                                onFetchRiders();
                            }}
                        />
                        특정 라이더만
                    </label>
                </div>

                {form.targetType === 'SPECIFIC' && (
                    <div className="rider-selector">
                        <input
                            type="text"
                            className="mission-input rider-search-input"
                            placeholder="라이더명 또는 ID 검색"
                            value={riderSearch}
                            onChange={(e) => onRiderSearchChange(e.target.value)}
                        />
                        <div className="rider-list">
                            {ridersLoading ? (
                                <span className="rider-empty">라이더 목록을 불러오는 중...</span>
                            ) : filteredRiders.length > 0 ? (
                                filteredRiders.map((rider) => (
                                    <label key={rider.riderId} className="rider-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={form.targetRiders.includes(rider.riderId)}
                                            onChange={() => onToggleRider(rider.riderId)}
                                        />
                                        {rider.riderName} ({rider.riderId})
                                    </label>
                                ))
                            ) : (
                                <span className="rider-empty">
                                    {riderSearch ? '검색 결과가 없습니다.' : '소속 라이더가 없습니다.'}
                                </span>
                            )}
                        </div>
                        {form.targetRiders.length > 0 && (
                            <div className="rider-selected-tags">
                                {form.targetRiders.map((id) => {
                                    const r = riders.find((x) => x.riderId === id);
                                    return (
                                        <span key={id} className="rider-tag">
                                            {r?.riderName ?? id}
                                            <button type="button" onClick={() => onToggleRider(id)}>
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 미션 미리보기 */}
            <div className="mission-preview">
                <div className="mission-preview-title">미션 미리보기</div>
                <div className="mission-preview-row">
                    <span className="mission-preview-key">미션</span>
                    <span className="mission-preview-value">{previewMission}</span>
                </div>
                <div className="mission-preview-row">
                    <span className="mission-preview-key">일정</span>
                    <span className="mission-preview-value">{previewSchedule}</span>
                </div>
                <div className="mission-preview-row">
                    <span className="mission-preview-key">보상</span>
                    <span className="mission-preview-value">{previewReward}</span>
                </div>
                <div className="mission-preview-row">
                    <span className="mission-preview-key">대상</span>
                    <span className="mission-preview-value">
                        {form.targetType === 'ALL'
                            ? '협력사 전체 라이더'
                            : form.targetRiders.length > 0
                                ? `특정 라이더 ${form.targetRiders.length}명`
                                : '특정 라이더 (미선택)'}
                    </span>
                </div>
            </div>

            {/* 버튼 */}
            <div className="mission-actions">
                <button type="button" className="mission-btn mission-btn-secondary" onClick={onReset}>
                    {editingSeqNo ? '수정 취소' : '초기화'}
                </button>
                <button
                    type="button"
                    className="mission-btn mission-btn-primary"
                    onClick={editingSeqNo ? onUpdate : onRegister}
                    disabled={!isValid}
                >
                    {editingSeqNo ? '수정 완료' : '+ 미션 등록'}
                </button>
            </div>
        </div>
    );
};

export default MissionRegisterForm;
